import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const HOST_COLORS = ['#FF6B35', '#00B4D8', '#4CAF50'];

const QUICK_INSTRUCTIONS = [
  '더 자연스럽게', '더 전문적으로', '더 재미있게', '더 짧게',
  '톤 밝게', '톤 진지하게',
];

export default function PodcastScriptViewer() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();

  const [data, setData] = useState(null);
  const [scriptId, setScriptId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeHost, setActiveHost] = useState(null);

  // 편집 모드
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  // AI 수정 모달
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiSelectedText, setAiSelectedText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  // 텍스트 선택 플로팅 버튼
  const [floatingBtn, setFloatingBtn] = useState(null); // {x, y, text}
  const scriptContainerRef = useRef(null);

  // 데이터 로드: routeId 있으면 DB, 없으면 sessionStorage
  useEffect(() => {
    const load = async () => {
      if (routeId) {
        try {
          const token = localStorage.getItem('accessToken');
          const res = await fetch(`/api/script/${routeId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (!res.ok) throw new Error('대본 로드 실패');
          const doc = await res.json();
          const pd = doc.podcastData || {};
          setData({
            script: pd.rawScript || doc.script?.intro || '',
            topic: pd.topic || doc.topic?.title || '',
            hosts: pd.hosts || [],
            duration: pd.duration,
            tone: pd.tone,
          });
          setScriptId(doc._id);
        } catch (e) {
          alert('대본 로드 실패: ' + e.message);
          navigate('/script/library');
        }
      } else {
        const raw = sessionStorage.getItem('podcastScript');
        if (!raw) { navigate('/podcast/setup'); return; }
        try {
          const parsed = JSON.parse(raw);
          setData(parsed);
          if (parsed.id) setScriptId(parsed.id);
        } catch { navigate('/podcast/setup'); }
      }
      setLoading(false);
    };
    load();
  }, [routeId, navigate]);

  // 텍스트 선택 감지
  useEffect(() => {
    const handleMouseUp = (e) => {
      if (editMode || aiModalOpen) return;
      const sel = window.getSelection();
      const text = sel?.toString().trim();
      if (!text || text.length < 5) {
        setFloatingBtn(null);
        return;
      }
      // 선택이 script 컨테이너 내부인지 확인
      if (!scriptContainerRef.current) return;
      const range = sel.getRangeAt(0);
      const node = range.commonAncestorContainer;
      if (!scriptContainerRef.current.contains(node.nodeType === 3 ? node.parentNode : node)) {
        setFloatingBtn(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      setFloatingBtn({
        x: rect.left + rect.width / 2,
        y: rect.top + window.scrollY - 8,
        text,
      });
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [editMode, aiModalOpen]);

  if (loading || !data) return (
    <div style={{ minHeight: '100vh', background: '#080C0F', color: '#6B7A8D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      로딩 중...
    </div>
  );

  const { script, topic, hosts, duration, tone } = data;
  const currentScript = editMode ? editText : script;

  // 대본 파싱: 섹션별로 분리
  const parseScript = (text) => {
    const lines = text.split('\n');
    const sections = [];
    let current = null;

    lines.forEach(line => {
      if (line.match(/^\[🎬|^\[📌|^\[💡|^\[🔥|^\[💬|^\[✅/)) {
        if (current) sections.push(current);
        current = { title: line, lines: [] };
      } else if (current) {
        current.lines.push(line);
      } else {
        if (!sections.length) sections.push({ title: '대본', lines: [] });
        sections[0].lines.push(line);
      }
    });
    if (current) sections.push(current);
    return sections;
  };

  const sections = parseScript(script);

  const getHostColor = (line) => {
    if (!hosts) return null;
    for (let i = 0; i < hosts.length; i++) {
      if (line.startsWith(`[${hosts[i].name}]`)) return HOST_COLORS[i];
    }
    return null;
  };

  const renderLine = (line, idx) => {
    if (!line.trim()) return <div key={idx} style={{ height: 8 }} />;

    const hostColor = getHostColor(line);
    if (hostColor) {
      const nameMatch = line.match(/^\[([^\]]+)\]/);
      const name = nameMatch?.[1] || '';
      const rest = line.replace(/^\[[^\]]+\]\s*/, '');
      return (
        <div key={idx} style={{
          display: 'flex', gap: 12, padding: '8px 0',
          opacity: activeHost && activeHost !== name ? 0.3 : 1,
          transition: 'opacity .2s',
        }}>
          <div style={{
            flexShrink: 0, width: 80, fontWeight: 700, fontSize: 13,
            color: hostColor, paddingTop: 2, fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: 1,
          }}>
            {name}
          </div>
          <div style={{ flex: 1, fontSize: 14, color: '#C8D0DA', lineHeight: 1.8 }}
            dangerouslySetInnerHTML={{ __html: rest.replace(/\*\*(.*?)\*\*/g, '<strong style="color:#E8EDF2">$1</strong>') }}
          />
        </div>
      );
    }

    if (line.startsWith('[♪')) return (
      <div key={idx} style={{ padding: '6px 12px', background: '#1A0E2A', borderRadius: 6, fontSize: 12, color: '#9C6FBF', fontStyle: 'italic', margin: '4px 0' }}>
        {line}
      </div>
    );

    if (line.startsWith('[전환]') || line.startsWith('(')) return (
      <div key={idx} style={{ fontSize: 12, color: '#FF9B35', fontStyle: 'italic', padding: '4px 0' }}>
        {line}
      </div>
    );

    return (
      <div key={idx} style={{ fontSize: 13, color: '#4A5568', padding: '2px 0' }}>
        {line}
      </div>
    );
  };

  const copyAll = () => {
    navigator.clipboard.writeText(currentScript);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const startEdit = () => {
    setEditText(script);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditText('');
  };

  const saveEdit = async () => {
    if (!scriptId) {
      // sessionStorage 모드: 로컬만 업데이트
      setData(prev => ({ ...prev, script: editText }));
      const raw = sessionStorage.getItem('podcastScript');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          parsed.script = editText;
          sessionStorage.setItem('podcastScript', JSON.stringify(parsed));
        } catch {}
      }
      setEditMode(false);
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/script/podcast/${scriptId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ rawScript: editText }),
      });
      if (!res.ok) throw new Error('저장 실패');
      setData(prev => ({ ...prev, script: editText }));
      setEditMode(false);
    } catch (e) {
      alert('저장 실패: ' + e.message);
    }
    setSaving(false);
  };

  const downloadDocx = async () => {
    if (!scriptId) {
      alert('DB에 저장된 대본만 워드 다운로드 가능합니다');
      return;
    }
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/script/podcast/download/${scriptId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('다운로드 실패');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `팟캐스트_${topic || '대본'}.docx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert('다운로드 실패: ' + e.message);
    }
  };

  const openAiModal = (selectedText = '') => {
    setAiSelectedText(selectedText);
    setAiInstruction('');
    setAiModalOpen(true);
    setFloatingBtn(null);
  };

  const runAiRewrite = async () => {
    if (!scriptId) {
      alert('DB에 저장된 대본만 AI 수정이 가능합니다');
      return;
    }
    if (!aiInstruction.trim()) {
      alert('수정 지시사항을 입력해주세요');
      return;
    }
    setAiLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/script/podcast/ai-rewrite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          scriptId,
          instruction: aiInstruction,
          selectedText: aiSelectedText || undefined,
        }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let result = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const obj = JSON.parse(line.slice(6));
            if (obj.type === 'done') result = obj.rewritten;
          } catch {}
        }
      }

      if (result != null) {
        if (aiSelectedText) {
          // 선택 부분만 교체
          const newScript = script.replace(aiSelectedText, result);
          setData(prev => ({ ...prev, script: newScript }));
          // PATCH로 저장
          const tk = localStorage.getItem('accessToken');
          await fetch(`/api/script/podcast/${scriptId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tk}` },
            body: JSON.stringify({ rawScript: newScript }),
          });
        } else {
          setData(prev => ({ ...prev, script: result }));
        }
      }
      setAiModalOpen(false);
      setAiInstruction('');
      setAiSelectedText('');
    } catch (e) {
      alert('AI 수정 실패: ' + e.message);
    }
    setAiLoading(false);
  };

  const S = {
    wrap: { minHeight: '100vh', background: '#080C0F', color: '#E8EDF2', fontFamily: "'Noto Sans KR', sans-serif" },
    header: {
      position: 'sticky', top: 0, zIndex: 10,
      background: '#080C0F', borderBottom: '1px solid #1E2A35',
      padding: '16px 32px', display: 'flex', alignItems: 'center', gap: 16,
    },
    title: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: '#FF6B35', letterSpacing: 1 },
    body: { maxWidth: 900, margin: '0 auto', padding: '32px' },

    hostsRow: { display: 'flex', gap: 12, marginBottom: 32 },
    hostBadge: (i, active) => ({
      flex: 1, padding: '16px 20px', borderRadius: 12,
      background: active ? `${HOST_COLORS[i]}15` : '#0E1520',
      border: `1px solid ${active ? HOST_COLORS[i] : '#1E2A35'}`,
      cursor: 'pointer', transition: 'all .2s',
    }),
    hostBadgeName: (i) => ({
      fontFamily: "'Bebas Neue', sans-serif", fontSize: 18,
      color: HOST_COLORS[i], letterSpacing: 1, marginBottom: 4,
    }),
    hostBadgeJob: { fontSize: 12, color: '#6B7A8D' },
    hostBadgeRole: (i) => ({ fontSize: 11, color: HOST_COLORS[i], marginTop: 4 }),

    meta: { display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' },
    metaItem: {
      padding: '6px 14px', background: '#0E1520', border: '1px solid #1E2A35',
      borderRadius: 100, fontSize: 12, color: '#6B7A8D',
    },

    section: {
      background: '#0E1520', border: '1px solid #1E2A35',
      borderRadius: 16, padding: 28, marginBottom: 20,
    },
    sectionTitle: {
      fontFamily: "'Bebas Neue', sans-serif", fontSize: 20,
      color: '#FF6B35', letterSpacing: 1, marginBottom: 20,
      paddingBottom: 12, borderBottom: '1px solid #1E2A35',
    },

    btnPrimary: {
      padding: '10px 20px', background: '#FF6B35', border: 'none',
      borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700,
      cursor: 'pointer', fontFamily: "'Noto Sans KR', sans-serif",
    },
    btnSecondary: {
      padding: '10px 20px', background: 'transparent',
      border: '1px solid #1E2A35', borderRadius: 8,
      color: '#6B7A8D', fontSize: 13, cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif",
    },
    btnAccent: {
      padding: '10px 20px', background: 'transparent',
      border: '1px solid #FF6B3560', borderRadius: 8,
      color: '#FF6B35', fontSize: 13, fontWeight: 600, cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif",
    },

    editTextarea: {
      width: '100%', minHeight: 600, padding: 24,
      background: '#080C0F', border: '1px solid #1E2A35', borderRadius: 12,
      color: '#E8EDF2', fontSize: 14, lineHeight: 1.8, outline: 'none',
      fontFamily: "'Noto Sans KR', sans-serif", resize: 'vertical',
      boxSizing: 'border-box',
    },

    // 모달
    modalBackdrop: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    modal: {
      background: '#0E1520', border: '1px solid #1E2A35',
      borderRadius: 16, padding: 32, width: 560, maxWidth: '90vw',
      maxHeight: '85vh', overflowY: 'auto',
    },
    modalTitle: {
      fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: '#FF6B35',
      letterSpacing: 1, marginBottom: 20,
    },
    modalLabel: { fontSize: 12, fontWeight: 600, color: '#6B7A8D', marginBottom: 6, display: 'block' },
    modalInput: {
      width: '100%', padding: 12, background: '#080C0F',
      border: '1px solid #1E2A35', borderRadius: 8, color: '#E8EDF2',
      fontSize: 13, outline: 'none', boxSizing: 'border-box',
      fontFamily: "'Noto Sans KR', sans-serif", minHeight: 80, resize: 'vertical',
    },
    quickGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 8 },
    quickBtn: {
      padding: '8px 12px', background: 'transparent',
      border: '1px solid #1E2A35', borderRadius: 8,
      color: '#C8D0DA', fontSize: 12, cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif",
    },
    selectedTextBox: {
      padding: 12, background: '#080C0F', border: '1px solid #FF6B3540',
      borderRadius: 8, fontSize: 12, color: '#C8D0DA', marginBottom: 16,
      maxHeight: 120, overflowY: 'auto', whiteSpace: 'pre-wrap',
    },
  };

  return (
    <div style={S.wrap}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Noto+Sans+KR:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* 상단 헤더 */}
      <div style={S.header}>
        <div style={S.title}>🎙 PODCAST SCRIPT</div>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 600, color: '#E8EDF2' }}>{topic}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {routeId ? (
            <button style={S.btnSecondary} onClick={() => navigate('/script/library')}>← 보관함</button>
          ) : (
            <button style={S.btnSecondary} onClick={() => navigate('/podcast/setup')}>← 다시 만들기</button>
          )}
          {!editMode && (
            <>
              <button style={S.btnSecondary} onClick={startEdit}>✏️ 수정</button>
              <button style={S.btnAccent} onClick={() => openAiModal('')} disabled={!scriptId}>
                ✨ AI 수정
              </button>
              <button style={S.btnSecondary} onClick={downloadDocx} disabled={!scriptId}>
                ⬇️ 워드 다운로드
              </button>
              <button style={{ ...S.btnPrimary, background: copied ? '#4CAF50' : '#FF6B35' }} onClick={copyAll}>
                {copied ? '✅ 복사됨' : '📋 전체 복사'}
              </button>
            </>
          )}
          {editMode && (
            <>
              <button style={S.btnSecondary} onClick={cancelEdit} disabled={saving}>취소</button>
              <button style={S.btnPrimary} onClick={saveEdit} disabled={saving}>
                {saving ? '저장 중...' : '💾 저장'}
              </button>
            </>
          )}
        </div>
      </div>

      <div style={S.body}>
        {/* 메타 정보 */}
        <div style={S.meta}>
          <div style={S.metaItem}>⏱ {duration}분</div>
          <div style={S.metaItem}>🎨 {tone}</div>
          <div style={S.metaItem}>👥 {hosts?.length}인 진행</div>
          <div style={S.metaItem}>📄 {currentScript.length.toLocaleString()}자</div>
          {!scriptId && <div style={{ ...S.metaItem, color: '#FF9B35', borderColor: '#FF9B3540' }}>⚠ 임시 저장</div>}
        </div>

        {/* 출연진 카드 */}
        <div style={S.hostsRow}>
          {hosts?.map((h, i) => (
            <div key={i} style={S.hostBadge(i, activeHost === h.name || !activeHost)}
              onClick={() => setActiveHost(activeHost === h.name ? null : h.name)}>
              <div style={S.hostBadgeName(i)}>{h.name}</div>
              <div style={S.hostBadgeJob}>{h.job}</div>
              <div style={S.hostBadgeRole(i)}>
                {['🎤','🎧','🎵'][i]} {h.roleType?.replace(/_/g, ' ')}
              </div>
              {activeHost === h.name && (
                <div style={{ fontSize: 10, color: '#FF6B35', marginTop: 4 }}>● 필터 중</div>
              )}
            </div>
          ))}
        </div>

        {/* 본문: 편집 모드 / 뷰 모드 */}
        {editMode ? (
          <textarea
            style={S.editTextarea}
            value={editText}
            onChange={e => setEditText(e.target.value)}
          />
        ) : (
          <div ref={scriptContainerRef}>
            {sections.map((sec, si) => (
              <div key={si} style={S.section}>
                <div style={S.sectionTitle}>{sec.title}</div>
                <div>
                  {sec.lines.map((line, li) => renderLine(line, li))}
                </div>
              </div>
            ))}

            {/* 분량 요약 */}
            <div style={{
              background: '#0E1520', border: '1px solid #FF6B3530',
              borderRadius: 16, padding: 24, marginTop: 8,
              display: 'flex', gap: 32, alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: '#FF6B35', letterSpacing: 1 }}>
                  {script.length.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: '#6B7A8D', marginTop: 2 }}>총 글자 수</div>
              </div>
              <div style={{ width: 1, height: 40, background: '#1E2A35' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: '#00B4D8', letterSpacing: 1 }}>
                  {Math.round(script.length / 175)}
                </div>
                <div style={{ fontSize: 12, color: '#6B7A8D', marginTop: 2 }}>예상 러닝타임 (분)</div>
              </div>
              <div style={{ width: 1, height: 40, background: '#1E2A35' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: '#4CAF50', letterSpacing: 1 }}>
                  {sections.length}
                </div>
                <div style={{ fontSize: 12, color: '#6B7A8D', marginTop: 2 }}>섹션 수</div>
              </div>
              <div style={{ width: 1, height: 40, background: '#1E2A35' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: '#9C27B0', letterSpacing: 1 }}>
                  {hosts?.length || 0}
                </div>
                <div style={{ fontSize: 12, color: '#6B7A8D', marginTop: 2 }}>출연진</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 텍스트 선택 플로팅 버튼 */}
      {floatingBtn && !editMode && (
        <button
          onClick={() => openAiModal(floatingBtn.text)}
          style={{
            position: 'absolute',
            left: floatingBtn.x, top: floatingBtn.y,
            transform: 'translate(-50%, -100%)',
            padding: '8px 14px', background: '#FF6B35', color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700,
            cursor: 'pointer', boxShadow: '0 4px 12px rgba(255,107,53,0.4)',
            fontFamily: "'Noto Sans KR', sans-serif", zIndex: 50,
          }}
        >
          ✨ AI 수정
        </button>
      )}

      {/* AI 수정 모달 */}
      {aiModalOpen && (
        <div style={S.modalBackdrop} onClick={() => !aiLoading && setAiModalOpen(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>✨ AI 수정</div>

            {aiSelectedText && (
              <div style={{ marginBottom: 16 }}>
                <label style={S.modalLabel}>선택된 텍스트</label>
                <div style={S.selectedTextBox}>{aiSelectedText}</div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={S.modalLabel}>수정 지시사항 *</label>
              <textarea
                style={S.modalInput}
                value={aiInstruction}
                onChange={e => setAiInstruction(e.target.value)}
                placeholder="예: 더 자연스럽게, 톤을 부드럽게, 비유를 추가해서..."
                disabled={aiLoading}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={S.modalLabel}>빠른 선택</label>
              <div style={S.quickGrid}>
                {QUICK_INSTRUCTIONS.map(q => (
                  <button key={q} style={S.quickBtn}
                    onClick={() => setAiInstruction(q)}
                    disabled={aiLoading}>
                    {q}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={S.btnSecondary}
                onClick={() => setAiModalOpen(false)}
                disabled={aiLoading}>
                취소
              </button>
              <button style={{ ...S.btnPrimary, opacity: aiLoading ? 0.6 : 1 }}
                onClick={runAiRewrite}
                disabled={aiLoading}>
                {aiLoading ? '🔄 AI 수정 중...' : '✨ AI 수정 적용'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
